import React, { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Building2, Mail, Phone, FileText, Globe, Edit2, Save, Trash2, Paperclip, Download, X, ExternalLink } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
} from "@/components/ui/dialog";

export default function LeadInfo({ lead, onUpdate, onDelete }) {
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({
    nombre: lead.nombre || "",
    empresa: lead.empresa || "",
    email: lead.email || "",
    telefono: lead.telefono || "",
  });
  const [imageOpen, setImageOpen] = useState(false);

  const isImageFile = (url) => {
    if (!url) return false;
    const ext = url.split('.').pop()?.toLowerCase().split('?')[0];
    return ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp'].includes(ext);
  };

  const getFileName = (url) => {
    if (!url) return 'archivo';
    return url.split('/').pop()?.split('?')[0] || 'archivo';
  };

  const paginaOrigen = lead.meta_webhook?.['URL de la página'] || null;

  const handleSave = async () => {
    await onUpdate({ id: lead.id, data: formData });
    setIsEditing(false);
  };

  return (
    <Card className="dark:bg-gray-900 dark:border-gray-800">
      <CardHeader>
        <CardTitle className="text-lg flex items-center justify-between text-gray-900 dark:text-white">
          Información del Lead
          <div className="flex items-center gap-2">
            {!isEditing ? (
              <Button variant="ghost" size="sm" onClick={() => setIsEditing(true)}>
                <Edit2 className="w-4 h-4" />
              </Button>
            ) : (
              <Button variant="ghost" size="sm" onClick={handleSave}>
                <Save className="w-4 h-4" />
              </Button>
            )}
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="ghost" size="sm">
                  <Trash2 className="w-4 h-4 text-red-500" />
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent className="dark:bg-gray-900">
                <AlertDialogHeader>
                  <AlertDialogTitle className="text-gray-900 dark:text-white">¿Eliminar lead?</AlertDialogTitle>
                  <AlertDialogDescription className="text-gray-600 dark:text-gray-400">
                    Esta acción no se puede deshacer. El lead "{lead.nombre}" y todos sus presupuestos serán eliminados permanentemente.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancelar</AlertDialogCancel>
                  <AlertDialogAction onClick={onDelete} className="bg-red-600 hover:bg-red-700">
                    Eliminar
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-start gap-3">
          <Building2 className="w-5 h-5 text-gray-400 mt-0.5" />
          <div className="flex-1">
            <p className="text-xs text-gray-500 dark:text-gray-400">Empresa/Nombre del Deal</p>
            {isEditing ? (
              <input
                type="text"
                value={formData.empresa}
                onChange={(e) => setFormData({ ...formData, empresa: e.target.value })}
                className="w-full mt-1 text-sm font-medium bg-transparent border-b border-gray-300 dark:border-gray-700 focus:border-indigo-500 outline-none text-gray-900 dark:text-white"
                placeholder="Nombre de la empresa o deal"
              />
            ) : (
              <p className="text-sm font-medium text-gray-900 dark:text-white">{lead.empresa || "Sin empresa"}</p>
            )}
          </div>
        </div>

        <div className="flex items-start gap-3">
          <Mail className="w-5 h-5 text-gray-400 mt-0.5" />
          <div className="flex-1">
            <p className="text-xs text-gray-500 dark:text-gray-400">Email</p>
            {isEditing ? (
              <input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="w-full mt-1 text-sm font-medium bg-transparent border-b border-gray-300 dark:border-gray-700 focus:border-indigo-500 outline-none text-gray-900 dark:text-white"
              />
            ) : (
              <p className="text-sm font-medium text-gray-900 dark:text-white">{lead.email}</p>
            )}
          </div>
        </div>

        <div className="flex items-start gap-3">
          <Phone className="w-5 h-5 text-gray-400 mt-0.5" />
          <div className="flex-1">
            <p className="text-xs text-gray-500 dark:text-gray-400">Teléfono</p>
            {isEditing ? (
              <input
                type="text"
                value={formData.telefono}
                onChange={(e) => setFormData({ ...formData, telefono: e.target.value })}
                className="w-full mt-1 text-sm font-medium bg-transparent border-b border-gray-300 dark:border-gray-700 focus:border-indigo-500 outline-none text-gray-900 dark:text-white"
              />
            ) : (
              <p className="text-sm font-medium text-gray-900 dark:text-white">{lead.telefono || "Sin teléfono"}</p>
            )}
          </div>
        </div>

        <div className="flex items-start gap-3">
          <Globe className="w-5 h-5 text-gray-400 mt-0.5" />
          <div>
            <p className="text-xs text-gray-500 dark:text-gray-400">Origen</p>
            <div className="flex items-center gap-2 mt-1">
              <Badge variant="outline">
                {lead.origen === 'email' ? '📧 Email' : lead.origen === 'web' ? '🌐 Web' : '✍️ Manual'}
              </Badge>
              {paginaOrigen && (
                <a 
                  href={paginaOrigen} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-xs text-indigo-600 dark:text-indigo-400 hover:underline flex items-center gap-1"
                >
                  {paginaOrigen.replace(/^https?:\/\//, '')}
                  <ExternalLink className="w-3 h-3" />
                </a>
              )}
            </div>
          </div>
        </div>

        {lead.archivo_adjunto && (
          <div className="flex items-start gap-3">
            <Paperclip className="w-5 h-5 text-gray-400 mt-0.5" />
            <div className="flex-1">
              <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">Archivo Adjunto</p>
              {isImageFile(lead.archivo_adjunto) ? (
                <>
                  <div 
                    className="cursor-pointer group relative inline-block"
                    onClick={() => setImageOpen(true)}
                  >
                    <img 
                      src={lead.archivo_adjunto} 
                      alt="Archivo adjunto"
                      className="max-w-[200px] max-h-[150px] rounded-lg border border-gray-200 dark:border-gray-700 object-cover hover:opacity-90 transition-opacity"
                    />
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 rounded-lg transition-colors flex items-center justify-center">
                      <span className="text-white opacity-0 group-hover:opacity-100 text-xs bg-black/50 px-2 py-1 rounded">
                        Click para ampliar
                      </span>
                    </div>
                  </div>
                  {imageOpen && (
                    <div 
                      className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center"
                      onClick={() => setImageOpen(false)}
                    >
                      <button
                        onClick={() => setImageOpen(false)}
                        className="absolute top-4 right-4 text-white hover:text-gray-300 z-50"
                      >
                        <X className="w-8 h-8" />
                      </button>
                      <a
                        href={lead.archivo_adjunto}
                        download={getFileName(lead.archivo_adjunto)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="absolute top-4 right-16 bg-white/20 hover:bg-white/30 p-2 rounded-full z-50"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <Download className="w-6 h-6 text-white" />
                      </a>
                      <img 
                        src={lead.archivo_adjunto} 
                        alt="Archivo adjunto"
                        className="max-w-[90vw] max-h-[90vh] object-contain"
                        onClick={(e) => e.stopPropagation()}
                      />
                    </div>
                  )}
                </>
              ) : (
                <a 
                  href={lead.archivo_adjunto} 
                  download={getFileName(lead.archivo_adjunto)}
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-sm text-indigo-600 dark:text-indigo-400 hover:underline bg-indigo-50 dark:bg-indigo-950 px-3 py-2 rounded-lg"
                >
                  <Download className="w-4 h-4" />
                  {getFileName(lead.archivo_adjunto)}
                </a>
              )}
            </div>
          </div>
        )}

        <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-1.5 mb-2">
            <Mail className="w-3 h-3 text-gray-500 dark:text-gray-400" />
            <p className="text-xs font-semibold text-gray-700 dark:text-gray-300">Email del cliente</p>
            <span className="text-[10px] text-gray-500 dark:text-gray-400">· mensaje original, no editable</span>
          </div>
          {lead.mensaje_original ? (
            <div className="bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-3 text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap max-h-[300px] overflow-y-auto">
              {lead.mensaje_original}
            </div>
          ) : (
            <div className="bg-gray-50 dark:bg-gray-800 border border-dashed border-gray-200 dark:border-gray-700 rounded-lg p-3 text-xs text-gray-400 italic">
              Sin mensaje original. Mira la pestaña de Emails para el hilo completo.
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}